import { zSidLabel } from "src/types";

import { zAttrValue, ZSafeDataMap } from "../attribute/ZAttrValue";
import { ZSpecification } from "./ZSpecification";

// Текстовые выражения, вычисляемые для указанной строки, в которой есть поле data

type RowWithData = {
  data?: ZSafeDataMap;
};

export type FnRowEvaluate<TRow extends RowWithData> = (row: TRow) => string;

export const makeRowEvaluate = <TRow extends RowWithData>(
  template: string,
  schemes: ZSpecification[],
): FnRowEvaluate<TRow> => {
  const pathDict: Record<string, string> = {};
  schemes.forEach((scheme) => {
    const schemeName = scheme.name;
    scheme.definition?.attributes.forEach((attr) => {
      const path = `data.${schemeName}.${attr.name}`;
      pathDict[`${schemeName}.${attr.name}`] = path;
      if (!pathDict[attr.name]) pathDict[attr.name] = path;
    });
  });

  const evalName = (row: TRow, name: string): string => {
    if (name !== "data" && name in row) {
      // Системные атрибуты имеют преимущество
      const value = textValue((row as Record<string, unknown>)[name]);
      if (value) return value;
    }
    const path = pathDict[name];
    if (path) {
      return textValue(getField(row, path));
    }
    return "";
  };

  return (row) =>
    template.replace(/{{([^}]*)}}/g, (_a, expr: string) => {
      const cases = expr.split("|").map((s) => s.trim());
      return cases.reduce((acc, name) => acc || evalName(row, name), "");
    });
};

const getField = (root: unknown, path: string): unknown => {
  if (!path) return root;
  const dotPos = path.indexOf(".");
  const [left, right] =
    dotPos < 0 ? [path, ""] : [path.slice(0, dotPos), path.slice(dotPos + 1)];
  if (!root || typeof root !== "object" || !(left in root)) return "";
  return getField((root as Record<string, unknown>)[left], right);
};

// Отличается от getScalarValue тем что поддерживает SafeValue и для strOption выдаёт label

const textValue = (srcValue: unknown): string => {
  const t = zAttrValue.safeParse(srcValue);
  if (t.success) {
    // eslint-disable-next-line default-case
    switch (t.data.type) {
      case "string":
        return t.data.value ?? "";
      case "number":
      case "boolean":
        return t.data.value === null || t.data.value === undefined
          ? ""
          : String(t.data.value);
      case "dateTime":
        return t.data.value ?? "";
      case "strOption":
        return t.data.value?.label ?? "";
      case "file":
        return t.data.value?.name ?? "";
    }
  }
  const sid = zSidLabel.safeParse(srcValue);
  if (sid.success) {
    return sid.data.label;
  }
  const value = JSON.parse(JSON.stringify(srcValue)); // без этого typeof всегда даёт object из-за Proxy
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
};
